// Styles
import "./FileUploads.scss";

// React Hooks
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import UploadIcon from "../../_assets/icons/UploadIcon";
import CheckCircleIcon from "../../_assets/icons/CheckCircleIcon";
import DeleteIcon from "../../_assets/icons/DeleteIcon";

// UI Components
import {
  Headline3,
  Headline2,
  Label2,
  Subtitle2,
  ButtonRound,
  Icon,
  Select,
  Loader,
} from "@bms-ai/bms-ai-ui-lib/components";
// Add Chevron icon import if available
// import { ChevronDownIcon, ChevronUpIcon } from "@bms-ai/bms-ai-ui-lib/icons";

// Components
// import Header from "../../components/header";
// import PreviewPane from "../../components/preview/PreviewPane/PreviewPane";
import StoryboardPreview from "../../components/preview/StoryboardPreview";
import ManuscriptPreview from "../../components/preview/ManuscriptPreview";
import BriefPreview from "../../components/preview/BriefPreview";
// import FigmaPreview from "../../components/preview/FigmaPreview";
import UploadModal from "../../components/UploadModal";
import SelectChecksModal from "../../components/SelectChecksModal/SelectChecksModal";

// Context
import { useAppContext } from "../../context/app-context";
import { useMultipleFileUpload } from "../../context/MultipleFileUploadContext";
import type {
  UploadedFiles,
  StoryboardFrame,
} from "../../context/MultipleFileUploadContext";
import { useDashboard } from "../../context/DashboardContext";

// API
// import { uploadBlob } from "../../utils/fileUploadApi";
import { useSession } from "../../context/session-context";
import {
  uploadFileToApi,
  downloadFrameImage,
} from "../../api/data-services/file-upload";
// import { createTaskNoTokenAPI } from "../../api/data-services/task-api";
// import { useQATask } from "../../context/QATaskContext";
import { createSanitizedFile, containsJson } from "../../helpers/fileUtils";
import {
  getStoryboardPreviews,
  getAnimatedBannerStoryboardPreviews,
  getManuscriptPreviews,
  getBriefPreviews,
  getFteStoryboardType,
} from "../../api/data-services/preview-api";
import { useCustomToast } from "../../components/common/toast";

// Types
type UploadType = keyof UploadedFiles;

type PendingUpload = {
  file: File; // Sanitized file for API upload
  originalFile: File; // Original file for display purposes
  uploadType: UploadType;
  progress: number;
  status: "uploading" | "complete" | "error";
  error?: string;
  response?: any;
};

const FileUploads: React.FC = () => {
  // Ref for preview panel and loader
  const previewPanelRef = useRef<HTMLDivElement | null>(null);
  const storyboardLoaderRef = useRef<HTMLDivElement | null>(null);
  const manuscriptLoaderRef = useRef<HTMLDivElement | null>(null);
  const briefLoaderRef = useRef<HTMLDivElement | null>(null);
  const previousChannelRef = useRef<typeof selectedChannel>(null);
  const previousBrandRef = useRef<typeof selectedBrand>(null);
  const previousRoleSelectionRef = useRef<typeof roleSelection | undefined>(
    undefined,
  );
  const navigate = useNavigate();
  const {
    selectedChannel,
    selectedBrand,
    setSelectedChannel,
    setSelectedBrand,
    roleSelection,
    setRoleSelection,
  } = useAppContext();
  const {
    uploadedFiles,
    setUploadedFiles,
    uploadResponses,
    setUploadResponses,
    previewData,
    setPreviewData,
    figmaDetails,
    setFigmaDetails,
  } = useMultipleFileUpload();
  const { sessionDetails, moduleAccessToken, roleName } = useSession();
  // const { setTaskId, setError, setTaskCreated } = useQATask();
  const { dashboardState } = useDashboard();
  const { brands, channels } = dashboardState;
  const { showToast } = useCustomToast();
  const [figmaUrlError, setFigmaUrlError] = useState<string>("");
  // const [creatingTask, setCreatingTask] = useState<boolean>(false);
  const [fteStoryboardTypeLoading, setFteStoryboardTypeLoading] =
    useState<boolean>(false);
  const [fteStoryboardType, setFteStoryboardType] = useState<
    "Figma" | "eWizard" | "unknown" | null
  >(null); // change the values figma/ewizard
  // Upload modal state
  const [uploadModal, setUploadModal] = useState<{
    isOpen: boolean;
    uploads: PendingUpload[];
    currentUploadType?: UploadType;
  }>({ isOpen: false, uploads: [] });

  const [dragActive, setDragActive] = useState({
    storyboard: false,
    manuscript: false,
    figmaFile: false,
    brief: false,
  });

  const [briefSheetExpanded, setBriefSheetExpanded] = useState<boolean>(true);
  const [storyboardExpanded, setStoryboardExpanded] = useState(true);
  const [manuscriptExpanded, setManuscriptExpanded] = useState(true);

  const fileInputRef = useRef<{
    storyboard: HTMLInputElement | null;
    manuscript: HTMLInputElement | null;
    figmaFile: HTMLInputElement | null;
    brief: HTMLInputElement | null;
  }>({
    storyboard: null,
    manuscript: null,
    figmaFile: null,
    brief: null,
  });

  const fteLabel = sessionDetails.region === "jp" ? "rte" : "fte";
  // Brand and Channel data from context
  const brandList =
    brands?.brands.map((brand) => ({
      brandId: brand.brand_id,
      brandName: brand.brand_name,
      brandLogoPath: "",
    })) || [];
  // const brandList = [
  //   { brandId: "1", brandName: "Camzyos", brandLogoPath: "" },
  //   { brandId: "2", brandName: "Reblozyl", brandLogoPath: "" },
  //   { brandId: "3", brandName: "Unbranded", brandLogoPath: "" },
  // ]

  // const channelList = [
  //   { channelId: "1", channelName: "Banner", subChannel: [] },
  //   { channelId: "2", channelName: "Animated Banner", subChannel: [] },
  //   { channelId: "3", channelName: "Email FTE", subChannel: [] },
  //   { channelId: "4", channelName: "Email HOE", subChannel: [] },
  // ]
  const channelList =
    channels?.channels.map((channel) => ({
      channelId: channel.channel_id,
      channelName: channel.channel_name,
      subChannel: [],
    })) || [];

  const validExtensions: Record<string, string[]> = {
    storyboard: ["pdf"],
    manuscript: ["doc", "docx"],
    figmaFile: ["fig", "pdf"],
    brief: ["xlsx", "xls"],
  };

  // Upload Requirements Configuration Table
  type UploadRequirements = {
    storyboard: boolean;
    manuscript: boolean;
    brief: boolean;
    figma: boolean;
  };

  // Default configuration for most brands
  const defaultUploadRequirements: Record<string, UploadRequirements> = {
    "Static Banner": {
      storyboard: true,
      manuscript: true,
      brief: true,
      figma: true,
    },
    "Animated Banner": {
      storyboard: true,
      manuscript: true,
      brief: true,
      figma: true,
    },
    "Email FTE": {
      storyboard: true,
      manuscript: true,
      brief: false,
      figma: false,
    },
    "Email RTE": {
      storyboard: true,
      manuscript: true,
      brief: false,
      figma: false,
    },
    "Email HOE": {
      storyboard: true,
      manuscript: true,
      brief: false,
      figma: true,
    },
  };

  // Brand-specific overrides (only for exceptions)
  const uploadRequirementsConfig: Record<
    string,
    Record<string, UploadRequirements>
  > = {
    Unbranded: {
      Banner: { storyboard: true, manuscript: true, brief: true, figma: false },
      "Animated Banner": {
        storyboard: true,
        manuscript: true,
        brief: true,
        figma: false,
      },
      "Email FTE": {
        storyboard: true,
        manuscript: true,
        brief: false,
        figma: false,
      },
      "Email RTE": {
        storyboard: true,
        manuscript: true,
        brief: false,
        figma: false,
      },
      "Email HOE": {
        storyboard: true,
        manuscript: true,
        brief: false,
        figma: false,
      },
    },
  };

  // Get upload requirements for current brand and channel selection
  const getUploadRequirements = (): UploadRequirements => {
    // If copywriter role is selected, only manuscript is available
    if (roleSelection === "copywriter") {
      return {
        storyboard: false,
        manuscript: true,
        brief: false,
        figma: false,
      };
    }

    const fallbackRequirements: UploadRequirements = {
      storyboard: true,
      manuscript: true,
      brief: true,
      figma: true,
    };

    if (!selectedBrand || !selectedChannel) {
      return fallbackRequirements;
    }

    // Check if brand has specific overrides, otherwise use default
    const brandConfig =
      uploadRequirementsConfig[selectedBrand.brandName] ||
      defaultUploadRequirements;

    const requirements = brandConfig[selectedChannel.label];
    return requirements || fallbackRequirements;
  };

  const uploadRequirements = getUploadRequirements();

  // Auto-clear data when fields become unavailable
  useEffect(() => {
    const requirements = getUploadRequirements();

    // Clear Storyboard data when Storyboard is not available
    if (!requirements.storyboard && uploadedFiles.storyboard) {
      setUploadedFiles((prev) => ({ ...prev, storyboard: null }));
      setUploadResponses((prev) => ({ ...prev, storyboard: null }));
      setPreviewData((prev) => ({ ...prev, storyboard: null }));
      setFteStoryboardType(null);
    }

    // Clear Figma details when Figma is not available
    if (!requirements.figma && (figmaDetails.url || figmaDetails.frameName)) {
      setFigmaDetails({ url: "", frameName: "", designNotes: "" });
      setFigmaUrlError("");
    }

    // Clear Brief data when Brief is not available
    if (!requirements.brief && uploadedFiles.brief) {
      setUploadedFiles((prev) => ({ ...prev, brief: null }));
      setUploadResponses((prev) => ({ ...prev, brief: null }));
      setPreviewData((prev) => ({ ...prev, brief: null }));
    }
  }, [selectedBrand, selectedChannel, roleSelection]);

  // Clear all uploads and previews when brand or channel actually changes to a different value
  useEffect(() => {
    // Skip clearing on initial mount
    if (
      previousChannelRef.current === null ||
      previousBrandRef.current === null
    ) {
      // First render - just set the refs
      previousChannelRef.current = selectedChannel;
      previousBrandRef.current = selectedBrand;
      return;
    }

    // Check if brand or channel actually changed to a different value
    const brandChanged =
      previousBrandRef.current?.brandId !== selectedBrand?.brandId;
    const channelChanged =
      previousChannelRef.current?.value !== selectedChannel?.value;

    if (brandChanged || channelChanged) {
      // Reset all uploaded files
      setUploadedFiles({
        storyboard: null,
        manuscript: null,
        figmaFile: null,
        brief: null,
      });

      // Reset all upload responses
      setUploadResponses({
        storyboard: null,
        manuscript: null,
        figmaFile: null,
        brief: null,
      });

      // Reset figma details
      setFigmaDetails({ url: "", frameName: "", designNotes: "" });
      setFigmaUrlError("");

      // Reset preview data
      setPreviewData({ storyboard: null, manuscript: null, brief: null });

      // Reset FTE storyboard type
      setFteStoryboardType(null);

      // Reset animated and static banner frames
      setAnimatedBannerFrames(null);
      setStaticBannerFrames(null);
    }

    // Update the refs for next comparison
    previousChannelRef.current = selectedChannel;
    previousBrandRef.current = selectedBrand;
  }, [selectedChannel, selectedBrand, setRoleSelection]);

  // Clear all uploads and figma inputs when role selection changes
  useEffect(() => {
    // Skip clearing on initial mount
    if (previousRoleSelectionRef.current === undefined) {
      previousRoleSelectionRef.current = roleSelection;
      return;
    }

    // Check if role selection actually changed
    const roleChanged = previousRoleSelectionRef.current !== roleSelection;

    if (roleChanged) {
      // Reset all uploaded files
      setUploadedFiles({
        storyboard: null,
        manuscript: null,
        figmaFile: null,
        brief: null,
      });

      // Reset all upload responses
      setUploadResponses({
        storyboard: null,
        manuscript: null,
        figmaFile: null,
        brief: null,
      });

      // Reset figma details
      setFigmaDetails({ url: "", frameName: "", designNotes: "" });
      setFigmaUrlError("");

      // Reset preview data
      setPreviewData({ storyboard: null, manuscript: null, brief: null });

      // Reset FTE storyboard type
      setFteStoryboardType(null);

      // Reset animated and static banner frames
      setAnimatedBannerFrames(null);
      setStaticBannerFrames(null);
    }

    // Update the ref for next comparison
    previousRoleSelectionRef.current = roleSelection;
  }, [roleSelection]);

  // Clear role selection if user doesn't have Operations, Admin, or Brief Admin role
  useEffect(() => {
    const allowedRoles = ["Operations", "Admin", "Brief Admin"];
    if (!allowedRoles.includes(roleName) && roleSelection) {
      setRoleSelection(null);
    }
  }, [roleName, setRoleSelection]);

  // Initialize roleSelection to default "qaoperator" for Operations, Admin, and Brief Admin users
  useEffect(() => {
    const allowedRoles = ["Operations", "Admin", "Brief Admin"];
    if (allowedRoles.includes(roleName) && !roleSelection) {
      setRoleSelection("qaoperator");
    }
  }, [roleName, roleSelection, setRoleSelection]);

  const handleUploadClick = (uploadType: UploadType): void => {
    const ref = fileInputRef.current[uploadType];
    if (ref) {
      ref.click();
    }
  };

  const handleDrag = (
    e: React.DragEvent<HTMLDivElement>,
    uploadType: UploadType,
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive((prev) => ({ ...prev, [uploadType]: true }));
    } else if (e.type === "dragleave") {
      setDragActive((prev) => ({ ...prev, [uploadType]: false }));
    }
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    uploadType: UploadType,
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive((prev) => ({ ...prev, [uploadType]: false }));

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      validateAndSetFile(file, uploadType);
    }
  };

  const validateAndSetFile = async (
    file: File,
    uploadType: UploadType,
  ): Promise<void> => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
    const fileName = file.name;

    if (fileName.length > 200) {
      alert("File name must not exceed 200 characters.");
      return;
    }

    if (file.size > 200 * 1024 * 1024) {
      alert("File size exceeds 200MB limit. Please select a smaller file.");
      return;
    } else if (!validExtensions[uploadType].includes(fileExtension)) {
      alert(`Invalid file type for ${uploadType}. Please upload a valid file.`);
      return;
    }

    // Sanitize the filename before upload (pass region for JP to allow Japanese characters)
    const sanitizedFile = createSanitizedFile(file);

    // Create pending upload and show modal
    const pendingUpload: PendingUpload = {
      file: sanitizedFile, // Use sanitized file for API upload
      originalFile: file, // Keep original file for display
      uploadType,
      progress: 0,
      status: "uploading",
    };

    // Open modal with this upload
    setUploadModal({
      isOpen: true,
      uploads: [pendingUpload],
      currentUploadType: uploadType,
    });

    // Start upload
    uploadFileInModal(pendingUpload);
  };

  const uploadFileInModal = async (
    pendingUpload: PendingUpload,
  ): Promise<void> => {
    const { file, uploadType } = pendingUpload;

    try {
      const token = sessionDetails.accessToken;
      // const uploadWorkGroupId = import.meta.env.VITE_UPLOAD_CONFIG_WORKGROUPID;
      const uploadClientId = import.meta.env.VITE_UPLOAD_CONFIG_CLIENTID;
      const uploadOrigin = import.meta.env.VITE_UPLOAD_CONFIG_ORIGIN;
      // TODO: Replace hardcoded values with dynamic ones as needed
      const response = await uploadFileToApi(
        file,
        // uploadWorkGroupId,
        sessionDetails.client_id || uploadClientId,
        uploadOrigin,
        token,
        (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) /
              (progressEvent.total || progressEvent.loaded),
          );
          // Cap progress at 90% until API confirms success/failure
          const cappedProgress = Math.min(percentCompleted, 90);
          // Update progress in modal
          setUploadModal((prev) => ({
            ...prev,
            uploads: prev.uploads.map((upload) =>
              upload.file === file
                ? { ...upload, progress: cappedProgress }
                : upload,
            ),
          }));
        },
      );
      console.log("Upload API response:", response);
      // Validate response structure
      if (!response?.success) {
        const errorMessage = response?.error;
        throw new Error(
          containsJson(errorMessage)
            ? "Upload failed. Please try again."
            : errorMessage || "Upload failed. Please try again.",
        );
      }

      if (!Array.isArray(response?.data) || response?.data?.length === 0) {
        throw new Error("Invalid response format from upload API");
      }

      // Extract upload data and update status to complete
      const uploadData = response.data[0];
      setUploadModal((prev) => ({
        ...prev,
        uploads: prev.uploads.map((upload) =>
          upload.file === file
            ? {
                ...upload,
                status: "complete",
                progress: 100,
                response: uploadData,
              }
            : upload,
        ),
      }));
    } catch (error) {
      console.error(`Upload failed for ${uploadType}:`, error);
      // Update status to error
      setUploadModal((prev) => ({
        ...prev,
        uploads: prev.uploads.map((upload) =>
          upload.file === file
            ? {
                ...upload,
                status: "error",
                error: error instanceof Error ? error.message : "Upload failed",
              }
            : upload,
        ),
      }));
    }
  };

  const validateFigmaUrl = (url: string): boolean => {
    const figmaUrlRegex =
      /^https:\/\/(www\.)?figma\.com\/(file|design|proto)\/[a-zA-Z0-9]+/;
    if (!url) {
      setFigmaUrlError("Figma URL is required.");
      return false;
    }
    if (!figmaUrlRegex.test(url)) {
      setFigmaUrlError("Please enter a valid Figma URL.");
      return false;
    }
    setFigmaUrlError("");
    return true;
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    uploadType: UploadType,
  ): void => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    validateAndSetFile(file, uploadType);
  };

  // Loader state for previews
  const [previewLoading, setPreviewLoading] = useState({
    storyboard: false,
    manuscript: false,
    brief: false,
  });

  // Store allFrames for animated banner storyboard
  const [animatedBannerFrames, setAnimatedBannerFrames] = useState<
    any[] | null
  >(null);

  // Store frames for static banner storyboard
  const [staticBannerFrames, setStaticBannerFrames] = useState<any[] | null>(
    null,
  );

  // Flag for animated banner storyboard preview readiness
  const [isAnimatedBannerStoryboardReady, setIsAnimatedBannerStoryboardReady] =
    useState(false);
  const [selectChecksPopupOpen, setSelectChecksPopupOpen] =
    useState<boolean>(false);

  // Scroll preview panel to loader when any preview loader is active
  useEffect(() => {
    // Scroll to the specific loader if visible
    setTimeout(() => {
      if (previewLoading.storyboard && storyboardLoaderRef.current) {
        storyboardLoaderRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (previewLoading.manuscript && manuscriptLoaderRef.current) {
        manuscriptLoaderRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (previewLoading.brief && briefLoaderRef.current) {
        briefLoaderRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  }, [
    previewLoading.storyboard,
    previewLoading.manuscript,
    previewLoading.brief,
  ]);

  const handleConfirmUploads = async (): Promise<void> => {
    // Move successful uploads to main area and fetch previews
    const previewPromises: Promise<void>[] = [];

    uploadModal.uploads.forEach((upload) => {
      if (upload.status === "complete" && upload.response) {
        // Store original file (for display) not sanitized file (used for API)
        setUploadedFiles((prev) => ({
          ...prev,
          [upload.uploadType]: upload.originalFile,
        }));
        setUploadResponses((prev) => ({
          ...prev,
          [upload.uploadType]: {
            DocID: upload.response.DocID,
            filename: upload.response.filename,
          },
        }));

        // Fetch FTE storyboard type for storyboard uploads in FTE channels
        const fetchFteStoryboardType = async () => {
          const docId = upload.response.DocID;
          const token = moduleAccessToken;
          const isFteChannel = selectedChannel?.label
            ?.toLowerCase()
            .includes(fteLabel);

          if (upload.uploadType === "storyboard" && isFteChannel) {
            try {
              setFteStoryboardTypeLoading(true);
              const fteType = await getFteStoryboardType(
                docId,
                sessionDetails.client_id || "",
                token,
              );
              console.log("FTE Storyboard Type:", fteType.fte_storyboard_type);
              setFteStoryboardType(fteType.fte_storyboard_type);
            } catch (error) {
              console.error("Failed to fetch FTE storyboard type:", error);
              setFteStoryboardType(null);
            } finally {
              setFteStoryboardTypeLoading(false);
            }
          }
        };

        // Fetch preview data for the uploaded file
        const fetchPreview = async () => {
          try {
            const docId = upload.response.DocID;
            const token = moduleAccessToken;

            switch (upload.uploadType) {
              case "storyboard":
                setPreviewLoading((prev) => ({ ...prev, storyboard: true }));
                setBriefSheetExpanded(false);
                setManuscriptExpanded(false);
                setStoryboardExpanded(true);
                if (selectedChannel) {
                  const channelId = selectedChannel.value;
                  const isAnimatedBanner =
                    selectedChannel.label === "Animated Banner";
                  // Paginated API call for animated banner storyboard
                  if (isAnimatedBanner) {
                    console.log(
                      "Fetching paginated storyboard preview for animated banner",
                    );
                    let allFrames: any[] = [];
                    let nextPageStart: number | null = 1;

                    // Keep fetching until next_page_start is null
                    while (nextPageStart !== null) {
                      const response =
                        await getAnimatedBannerStoryboardPreviews(
                          channelId,
                          docId,
                          sessionDetails.client_id || "",
                          token,
                          nextPageStart,
                          nextPageStart, // page_end same as page_start for single page fetch
                        );

                      // Accumulate frames
                      if (response.frames && Array.isArray(response.frames)) {
                        allFrames = [...allFrames, ...response.frames];
                      }

                      // Update next page to fetch
                      nextPageStart = response.next_page_start;

                      console.log(
                        `Fetched ${response.frames?.length || 0} frames, next_page_start: ${nextPageStart}`,
                      );
                    }

                    // Don't set frames here - let the preview component handle dimension-based selection
                    // Preview will call handleFrameSelectionChange with selected frames

                    // Fetch frame images for all accumulated frames
                    const framesWithImages = await Promise.all(
                      allFrames.map(async (frame) => {
                        const frameDocId = frame.doc_id;
                        let imageUrl = null;
                        if (frameDocId) {
                          imageUrl = await downloadFrameImage(
                            frameDocId,
                            sessionDetails.client_id || "",
                            token,
                          );
                        }
                        return { ...frame, imageUrl };
                      }),
                    );

                    setPreviewData((prev) => ({
                      ...prev,
                      storyboard: framesWithImages,
                    }));
                    setPreviewLoading((prev) => ({
                      ...prev,
                      storyboard: false,
                    }));

                    // Set storyboard ready flag
                    setIsAnimatedBannerStoryboardReady(
                      Array.isArray(allFrames) && allFrames.length > 0,
                    );
                    // Show toast notification if frames exceed 20
                    if (allFrames.length > 20) {
                      showToast(
                        "The uploaded storyboard file has >20 frames. Please upload a file with 20 or lesser frames. If you wish to proceed, select sizes adding up to a maximum of 20 frames.",
                        10000,
                      );
                    }
                  } else if (selectedChannel.label === "Static Banner") {
                    // Static banner - single API call, not paginated
                    const response = await getStoryboardPreviews(
                      channelId,
                      docId,
                      sessionDetails.client_id || "",
                      token,
                    );

                    // Extract frames from response (API returns { frames: [...] })
                    const frames = Array.isArray(response)
                      ? response
                      : (response as any).frames || [];

                    // Save frames for static banner storyboard
                    setStaticBannerFrames(frames);
                    setIsAnimatedBannerStoryboardReady(true);

                    // Fetch frame images for each frame if docid is present
                    const framesWithImages = await Promise.all(
                      frames.map(async (frame: any) => {
                        const frameDocId = frame.doc_id;
                        let imageUrl = null;
                        if (frameDocId) {
                          imageUrl = await downloadFrameImage(
                            frameDocId,
                            undefined,
                            token,
                          );
                        }
                        return { ...frame, imageUrl };
                      }),
                    );

                    setPreviewData((prev) => ({
                      ...prev,
                      storyboard: framesWithImages,
                    }));
                    setPreviewLoading((prev) => ({
                      ...prev,
                      storyboard: false,
                    }));
                  } else {
                    // Other channels
                    setIsAnimatedBannerStoryboardReady(true);
                    // Single API call for other channels - returns direct array
                    const frames = await getStoryboardPreviews(
                      channelId,
                      docId,
                      sessionDetails.client_id || "",
                      token,
                    );

                    // Fetch frame images for each frame if docid is present
                    const framesWithImages = await Promise.all(
                      frames.map(async (frame: any) => {
                        const frameDocId = frame.doc_id;
                        let imageUrl = null;
                        if (frameDocId) {
                          imageUrl = await downloadFrameImage(
                            frameDocId,
                            undefined,
                            token,
                          );
                        }
                        return { ...frame, imageUrl };
                      }),
                    );

                    setPreviewData((prev) => ({
                      ...prev,
                      storyboard: framesWithImages,
                    }));
                    setPreviewLoading((prev) => ({
                      ...prev,
                      storyboard: false,
                    }));
                  }
                }
                break;

              case "manuscript":
                setPreviewLoading((prev) => ({ ...prev, manuscript: true }));
                setStoryboardExpanded(false);
                setBriefSheetExpanded(false);
                setManuscriptExpanded(true);
                const manuscriptPreview = await getManuscriptPreviews(
                  docId,
                  sessionDetails.client_id || "",
                  token,
                  selectedChannel?.value,
                );
                setPreviewData((prev) => ({
                  ...prev,
                  manuscript: manuscriptPreview,
                }));
                setPreviewLoading((prev) => ({ ...prev, manuscript: false }));
                break;

              case "brief":
                setPreviewLoading((prev) => ({ ...prev, brief: true }));
                setStoryboardExpanded(false);
                setManuscriptExpanded(false);
                setBriefSheetExpanded(true);
                const briefPreview = await getBriefPreviews(
                  docId,
                  sessionDetails.client_id || "",
                  token,
                );
                setPreviewData((prev) => ({
                  ...prev,
                  brief: briefPreview,
                }));
                setPreviewLoading((prev) => ({ ...prev, brief: false }));
                break;

              default:
                // No preview for other file types
                break;
            }
          } catch (error) {
            setPreviewLoading((prev) => ({
              ...prev,
              [upload.uploadType]: false,
            }));
            console.error(
              `Failed to fetch preview for ${upload.uploadType}:`,
              error,
            );
            // Don't block the upload confirmation on preview fetch failure
          }
        };

        previewPromises.push(fetchFteStoryboardType());
        previewPromises.push(fetchPreview());
      }
    });

    // Close modal immediately, don't wait for previews
    setUploadModal({
      isOpen: false,
      uploads: [],
      currentUploadType: undefined,
    });

    // Fetch previews in background
    await Promise.allSettled(previewPromises);
  };

  const handleCancelUploads = (): void => {
    // Close modal and discard uploads
    setUploadModal({
      isOpen: false,
      uploads: [],
      currentUploadType: undefined,
    });
  };

  const handleRemoveUpload = (index: number): void => {
    setUploadModal((prev) => ({
      ...prev,
      uploads: prev.uploads.filter((_, i) => i !== index),
    }));
  };

  const handleAddDocumentFromModal = (): void => {
    // Trigger file selection for the same upload type that opened the modal
    if (uploadModal.currentUploadType) {
      handleUploadClick(uploadModal.currentUploadType);
    }
  };

  const handleRemoveUploadedFile = (uploadType: UploadType): void => {
    setUploadedFiles((prev) => ({
      ...prev,
      [uploadType]: null,
    }));
    setUploadResponses((prev) => ({
      ...prev,
      [uploadType]: null,
    }));
    // Clear preview data for the removed file
    if (
      uploadType === "storyboard" ||
      uploadType === "manuscript" ||
      uploadType === "brief"
    ) {
      setPreviewData((prev) => ({
        ...prev,
        [uploadType]: null,
      }));
    }
    // Clear FTE storyboard type if storyboard is removed
    if (uploadType === "storyboard") {
      setFteStoryboardType(null);
      setAnimatedBannerFrames(null);
      setStaticBannerFrames(null);
    }
  };

  const handleFrameSelectionChange = useCallback(
    (selectedFrames: StoryboardFrame[]): void => {
      const isAnimatedBanner = selectedChannel?.label === "Animated Banner";

      // Update animated banner frames with user selection
      if (isAnimatedBanner) {
        setAnimatedBannerFrames(selectedFrames);
      }
    },
    [selectedChannel, setAnimatedBannerFrames],
  );
  // const handleNext = async (): Promise<void> => {
  //   // Get brand_id and channel_id for backend
  //   const brandId = selectedBrand?.brandId || "";
  //   const channelId = selectedChannel?.value || "";
  //   const isFteChannel = selectedChannel?.label
  //     ?.toLowerCase()
  //     .includes(fteLabel);

  //   // Create payload for QA processing
  //   const payload: any = {
  //     storyboard_file_id: uploadResponses.storyboard?.DocID || "",
  //     storyboard_file_name: uploadResponses.storyboard?.filename || "",
  //     manuscript_file_id: uploadResponses.manuscript?.DocID || "",
  //     manuscript_file_name: uploadResponses.manuscript?.filename || "",
  //     brief_file_id: uploadResponses.brief?.DocID || "",
  //     brief_file_name: uploadResponses.brief?.filename || "",
  //     figma_url: figmaDetails.url || "",
  //     figma_page_name: figmaDetails.frameName || "",
  //     brand_id: brandId,
  //     channel_id: channelId,
  //     client_id: sessionDetails.client_id || "",
  //     user_id: sessionDetails.user_id || "",
  //     enterpriseid: sessionDetails.enterpriseid || "",
  //   };

  //   // Add role if Operations, Admin, or Brief Admin user (default to qaoperator if not explicitly selected)
  //   const allowedRoles = ["Operations", "Admin", "Brief Admin"];
  //   if (allowedRoles.includes(roleName)) {
  //     payload.role = roleSelection || "qaoperator";
  //   }

  //   // Add FTE storyboard type if channel is FTE and storyboard is uploaded
  //   if (isFteChannel && uploadedFiles.storyboard && fteStoryboardType) {
  //     payload.fte_storyboard_type = fteStoryboardType.toLowerCase();
  //   }

  //   // For animated banner storyboard, add frames to payload (property name: frames)
  //   const isAnimatedBanner = selectedChannel?.label === "Animated Banner";
  //   if (
  //     isAnimatedBanner &&
  //     animatedBannerFrames &&
  //     Array.isArray(animatedBannerFrames)
  //   ) {
  //     console.log(
  //       "Adding animated banner frames to payload as 'frames':",
  //       animatedBannerFrames,
  //     );
  //     payload.frames = animatedBannerFrames;
  //   }

  //   // Create task and get task_id
  //   setCreatingTask(true);
  //   try {
  //     const response = await createTaskNoTokenAPI(payload, moduleAccessToken);

  //     if (response && response.task_id) {
  //       setTaskId(response.task_id);
  //       setTaskCreated(response.task_id);
  //       navigate("/qa-results");
  //     } else {
  //       throw new Error("Invalid response format - missing task_id");
  //     }
  //   } catch (error) {
  //     console.error("Failed to create task:", error);
  //     const errorMessage =
  //       error instanceof Error ? error.message : "Failed to create QA task";
  //     setError(errorMessage);
  //     alert(`Failed to create QA task: ${errorMessage}`);
  //   } finally {
  //     setCreatingTask(false);
  //   }
  // };

  const handleBack = (): void => {
    navigate("/dashboard");
  };

  return (
    <div className="FileUploadsContainer">
      {/* Header */}
      <div className="upload-header">
        <button className="back-link" onClick={handleBack}>
          <Icon type="arrow-left" size="small" />
          <span>Content QA Dashboard</span>
        </button>
      </div>

      {/* Main Title */}
      <div className="main-title-section">
        <Headline2 className="main-title">Provide Inputs for QA</Headline2>
        <div className="button-group">
          <ButtonRound
            className="select-checks-btn"
            children="Continue"
            onClick={() => setSelectChecksPopupOpen(true)}
            disabled={
              (
                // Basic prerequisites
                !selectedBrand ||
                !selectedChannel ||
                // At least one required field must be provided
                !(
                  (uploadRequirements.storyboard && uploadedFiles.storyboard) ||
                  (uploadRequirements.manuscript && uploadedFiles.manuscript) ||
                  (uploadRequirements.brief && uploadedFiles.brief) ||
                  (uploadRequirements.figma &&
                    figmaDetails.url &&
                    figmaDetails.frameName)
                ) ||
                // QA operator cannot proceed with manuscript alone
                (roleSelection === "qaoperator" &&
                  uploadedFiles.manuscript &&
                  !uploadedFiles.storyboard &&
                  !uploadedFiles.brief &&
                  !figmaDetails.url &&
                  !(
                    selectedChannel?.label === "Animated Banner" &&
                    uploadedFiles.storyboard
                  )) ||
                // Disable while FTE storyboard type is being determined
                (fteStoryboardTypeLoading ?? false) ||
                // Preview validation: disable until storyboard preview is loaded (only if storyboard is uploaded)
                ((selectedChannel?.label === "Animated Banner" ||
                  selectedChannel?.label === "Static Banner") &&
                  uploadedFiles.storyboard &&
                  !isAnimatedBannerStoryboardReady) ||
                // Preview validation: If animated banner with storyboard, disable if no frames selected
                (selectedChannel?.label === "Animated Banner" &&
                  uploadedFiles.storyboard &&
                  (!animatedBannerFrames || animatedBannerFrames.length === 0)) ||
                // Preview validation: If static banner with storyboard, disable if no frames selected
                (selectedChannel?.label === "Static Banner" &&
                  uploadedFiles.storyboard &&
                  (!staticBannerFrames || staticBannerFrames.length === 0))
              ) as boolean
            }
          />
          {/* Run analysis button logic moved to SelectChecksModal Confirm button */}
          {/* <ButtonRound
            className={`run-analysis-btn ${creatingTask ? "creating-task" : ""}`}
            children={creatingTask ? "Creating Task..." : "Run analysis"}
            onClick={handleNext}
            disabled={
              creatingTask ||
              fteStoryboardTypeLoading ||
              !selectedBrand ||
              !selectedChannel ||
              (selectedChannel?.label === "Animated Banner" &&
                uploadedFiles.storyboard &&
                !isAnimatedBannerStoryboardReady) ||
              (roleSelection === "qaoperator" &&
                uploadedFiles.manuscript &&
                !uploadedFiles.storyboard &&
                !uploadedFiles.brief &&
                !figmaDetails.url &&
                !(
                  selectedChannel?.label === "Animated Banner" &&
                  uploadedFiles.storyboard
                )) ||
              !(
                (uploadRequirements.storyboard && uploadedFiles.storyboard) ||
                (uploadRequirements.manuscript && uploadedFiles.manuscript) ||
                (uploadRequirements.brief && uploadedFiles.brief) ||
                (uploadRequirements.figma &&
                  figmaDetails.url &&
                  figmaDetails.frameName)
              )
            }
          /> */}
        </div>
      </div>

      <div className="upload-content">
        {/* Left Panel - Upload Assets */}
        <div className="upload-assets-panel">
          <div className="panel-header-sticky">
            <Headline3 className="panel-title">Upload assets</Headline3>
            <Subtitle2 className="panel-subtitle">
              Upload storyboard and manuscript files that will be included in
              Content QA.
            </Subtitle2>
          </div>

          {/* Brand & Channel Selection */}
          <div className="brand-channel-section">
            {/* Role Selection - Only visible for Operations, Admin, and Brief Admin roles */}
            {(roleName === "Operations" ||
              roleName === "Admin" ||
              roleName === "Brief Admin") && (
              <div className="form-group">
                <div className="field-label-with-icon">
                  <Label2 strong className="field-label">
                    Role
                  </Label2>
                </div>
                <Select
                  className="select-dropdown"
                  options={[
                    { label: "QA Operator", value: "qaoperator" },
                    { label: "Copy Writer", value: "copywriter" },
                  ]}
                  preSelectedOption={
                    roleSelection
                      ? {
                          label:
                            roleSelection === "copywriter"
                              ? "Copy Writer"
                              : "QA Operator",
                          value: roleSelection,
                        }
                      : { label: "QA Operator", value: "qaoperator" }
                  }
                  onSelect={(option) => {
                    if (option.value === "copywriter") {
                      setRoleSelection("copywriter");
                    } else if (option.value === "qaoperator") {
                      setRoleSelection("qaoperator");
                    } else {
                      setRoleSelection(null);
                    }
                  }}
                />
              </div>
            )}

            <Label2 strong className="section-title">
              Select Brand & Channel
            </Label2>

            <div className="form-group">
              <div className="field-label-with-icon">
                <Label2 strong className="field-label">
                  Brand
                </Label2>
              </div>
              <Select
                className="select-dropdown"
                options={brandList.map((brand) => ({
                  label: brand.brandName,
                  value: String(brand.brandId),
                }))}
                preSelectedOption={
                  !brands
                    ? { label: "Loading brands...", value: "" }
                    : selectedBrand
                      ? {
                          label: selectedBrand.brandName,
                          value: String(selectedBrand.brandId),
                        }
                      : { label: "Select brand", value: "" }
                }
                onSelect={(option) => {
                  const brand = brandList.find(
                    (b) => String(b.brandId) === option.value,
                  );
                  setSelectedBrand(brand || null);
                }}
              />
            </div>

            <div className="form-group">
              <div className="field-label-with-icon">
                <Label2 strong className="field-label">
                  Channel
                </Label2>
              </div>
              <Select
                className="select-dropdown"
                options={channelList.map((ch) => ({
                  label: ch.channelName,
                  value: String(ch.channelId),
                }))}
                preSelectedOption={
                  !channels
                    ? { label: "Loading channels...", value: "" }
                    : selectedChannel
                      ? selectedChannel
                      : { label: "Select channel", value: "" }
                }
                onSelect={(option) => setSelectedChannel(option)}
              />
            </div>
          </div>

          {/* Upload Assets Section */}
          <div className="upload-section">
            <Label2 strong className="section-title">
              Upload Assets
            </Label2>

            {/* Storyboard Upload */}
            {uploadRequirements.storyboard && (
              <div className="upload-item">
                <Label2 strong className="upload-label">
                  Storyboard
                </Label2>

                <div
                  className={`upload-dropzone ${dragActive.storyboard ? "drag-active" : ""} ${!selectedBrand || !selectedChannel ? "disabled" : ""}`}
                  onDragEnter={(e) => {
                    if (selectedBrand && selectedChannel)
                      handleDrag(e, "storyboard");
                  }}
                  onDragLeave={(e) => {
                    if (selectedBrand && selectedChannel)
                      handleDrag(e, "storyboard");
                  }}
                  onDragOver={(e) => {
                    if (selectedBrand && selectedChannel)
                      handleDrag(e, "storyboard");
                  }}
                  onDrop={(e) => {
                    if (selectedBrand && selectedChannel)
                      handleDrop(e, "storyboard");
                  }}
                  onClick={() => {
                    if (selectedBrand && selectedChannel)
                      handleUploadClick("storyboard");
                  }}
                >
                  <div className="upload-area-content">
                    <UploadIcon className="upload-svg-icon" />
                    <div className="upload-text-wrapper">
                      <div className="upload-text">
                        <span className="upload-link">Click to upload</span> or
                        drag and drop files here.
                      </div>
                      <div className="upload-hint">PDF (max 200MB)</div>
                    </div>
                  </div>

                  {uploadedFiles.storyboard && (
                    <div className="uploaded-file-display">
                      <div className="upload-success-container">
                        <CheckCircleIcon className="check-icon" />
                        <div className="uploaded-file-info">
                          <span className="file-name">
                            {uploadedFiles.storyboard.name.length > 30
                              ? uploadedFiles.storyboard.name.slice(0, 30) +
                                "..."
                              : uploadedFiles.storyboard.name}
                          </span>
                          <span className="upload-status">Upload complete</span>
                        </div>
                      </div>
                      <DeleteIcon
                        className="remove-icon"
                        fill="#BE2BBB"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveUploadedFile("storyboard");
                        }}
                      />
                    </div>
                  )}

                  <input
                    type="file"
                    ref={(el) => {
                      if (fileInputRef.current)
                        fileInputRef.current.storyboard = el;
                    }}
                    onChange={(e) => handleFileChange(e, "storyboard")}
                    style={{ display: "none" }}
                    accept=".pdf"
                  />
                </div>

                {/* FTE Storyboard Type Display */}
                {uploadedFiles.storyboard &&
                  selectedChannel?.label?.toLowerCase().includes(fteLabel) &&
                  !fteStoryboardTypeLoading &&
                  fteStoryboardType && (
                    <div className="fte-storyboard-type-container">
                      <Label2 strong className="fte-type-label">
                        {sessionDetails.region === "jp"
                          ? "RTE Storyboard Type"
                          : "FTE Storyboard Type"}
                      </Label2>
                      <div className={`fte-type-badge ${fteStoryboardType}`}>
                        {fteStoryboardType}
                      </div>
                    </div>
                  )}
              </div>
            )}

            {/* Manuscript Upload */}
            <div className="upload-item">
              <Label2 strong className="upload-label">
                Manuscript
              </Label2>

              <div
                className={`upload-dropzone ${dragActive.manuscript ? "drag-active" : ""} ${!selectedBrand || !selectedChannel ? "disabled" : ""}`}
                onDragEnter={(e) => {
                  if (selectedBrand && selectedChannel)
                    handleDrag(e, "manuscript");
                }}
                onDragLeave={(e) => {
                  if (selectedBrand && selectedChannel)
                    handleDrag(e, "manuscript");
                }}
                onDragOver={(e) => {
                  if (selectedBrand && selectedChannel)
                    handleDrag(e, "manuscript");
                }}
                onDrop={(e) => {
                  if (selectedBrand && selectedChannel)
                    handleDrop(e, "manuscript");
                }}
                onClick={() => {
                  if (selectedBrand && selectedChannel)
                    handleUploadClick("manuscript");
                }}
              >
                <div className="upload-area-content">
                  <UploadIcon className="upload-svg-icon" />
                  <div className="upload-text-wrapper">
                    <div className="upload-text">
                      <span className="upload-link">Click to upload</span> or
                      drag and drop files here.
                    </div>
                    <div className="upload-hint">DOC or DOCX (max 200MB)</div>
                  </div>
                </div>

                {uploadedFiles.manuscript && (
                  <div className="uploaded-file-display">
                    <div className="upload-success-container">
                      <CheckCircleIcon className="check-icon" />
                      <div className="uploaded-file-info">
                        <span className="file-name">
                          {uploadedFiles.manuscript.name.length > 30
                            ? uploadedFiles.manuscript.name.slice(0, 30) + "..."
                            : uploadedFiles.manuscript.name}
                        </span>
                        <span className="upload-status">Upload complete</span>
                      </div>
                    </div>
                    <DeleteIcon
                      className="remove-icon"
                      fill="#BE2BBB"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveUploadedFile("manuscript");
                      }}
                    />
                  </div>
                )}

                <input
                  type="file"
                  ref={(el) => {
                    if (fileInputRef.current)
                      fileInputRef.current.manuscript = el;
                  }}
                  onChange={(e) => handleFileChange(e, "manuscript")}
                  style={{ display: "none" }}
                  accept=".doc,.docx"
                />
              </div>
            </div>
          </div>

          {/* Figma Details Section - Visible only when required */}
          {uploadRequirements.figma && (
            <div className="figma-details-section">
              <Label2 strong className="section-title">
                Figma details
              </Label2>

              <div className="figma-field">
                <div className="field-label-with-icon">
                  <Label2 strong className="field-label">
                    Figma link
                  </Label2>
                </div>
                <input
                  type="text"
                  className="figma-input"
                  placeholder="Figma link"
                  value={figmaDetails.url}
                  onChange={(e) => {
                    setFigmaDetails((prev) => ({
                      ...prev,
                      url: e.target.value,
                    }));
                    if (figmaUrlError) validateFigmaUrl(e.target.value);
                  }}
                  onBlur={(e) =>
                    e.target.value && validateFigmaUrl(e.target.value)
                  }
                  disabled={!selectedBrand || !selectedChannel}
                />
                {figmaUrlError && (
                  <span className="error-text">{figmaUrlError}</span>
                )}
              </div>

              <div className="figma-field">
                <div className="field-label-with-icon">
                  <Label2 strong className="field-label">
                    Page name
                  </Label2>
                </div>
                <input
                  type="text"
                  className="figma-input"
                  placeholder="Page name"
                  value={figmaDetails.frameName}
                  onChange={(e) =>
                    setFigmaDetails((prev) => ({
                      ...prev,
                      frameName: e.target.value,
                    }))
                  }
                  disabled={!selectedBrand || !selectedChannel}
                />
              </div>

              {/* <div className="figma-field">
                <div className="field-label-with-icon">
                  <Label2 strong className="field-label">
                    Component name
                  </Label2>
                </div>
                <input
                  type="text"
                  className="figma-input"
                  placeholder="Component name"
                  value={figmaDetails.designNotes}
                  onChange={(e) =>
                    setFigmaDetails((prev) => ({
                      ...prev,
                      designNotes: e.target.value,
                    }))
                  }
                  disabled={!selectedBrand || !selectedChannel}
                />
              </div> */}
            </div>
          )}

          {/* Brief Upload Section - Conditional based on brand and channel */}
          {uploadRequirements.brief && (
            <div className="brief-section">
              <div className="upload-item">
                <Label2 strong className="section-title">
                  Brief
                </Label2>
                <div
                  className={`upload-dropzone ${dragActive.brief ? "drag-active" : ""} ${!selectedBrand || !selectedChannel ? "disabled" : ""}`}
                  onDragEnter={(e) => {
                    if (selectedBrand && selectedChannel)
                      handleDrag(e, "brief");
                  }}
                  onDragLeave={(e) => {
                    if (selectedBrand && selectedChannel)
                      handleDrag(e, "brief");
                  }}
                  onDragOver={(e) => {
                    if (selectedBrand && selectedChannel)
                      handleDrag(e, "brief");
                  }}
                  onDrop={(e) => {
                    if (selectedBrand && selectedChannel)
                      handleDrop(e, "brief");
                  }}
                  onClick={() => {
                    if (selectedBrand && selectedChannel)
                      handleUploadClick("brief");
                  }}
                >
                  <div className="upload-area-content">
                    <UploadIcon className="upload-svg-icon" />
                    <div className="upload-text-wrapper">
                      <div className="upload-text">
                        <span className="upload-link">Click to upload</span> or
                        drag and drop files here.
                      </div>
                      <div className="upload-hint">XLS or XLSX (max 200MB)</div>
                    </div>
                  </div>

                  {uploadedFiles.brief && (
                    <div className="uploaded-file-display">
                      <div className="upload-success-container">
                        <CheckCircleIcon className="check-icon" />
                        <div className="uploaded-file-info">
                          <span className="file-name">
                            {uploadedFiles.brief.name.length > 30
                              ? uploadedFiles.brief.name.slice(0, 30) + "..."
                              : uploadedFiles.brief.name}
                          </span>
                          <span className="upload-status">Upload complete</span>
                        </div>
                      </div>
                      <DeleteIcon
                        className="remove-icon"
                        fill="#BE2BBB"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveUploadedFile("brief");
                        }}
                      />
                    </div>
                  )}

                  <input
                    type="file"
                    ref={(el) => {
                      if (fileInputRef.current) fileInputRef.current.brief = el;
                    }}
                    onChange={(e) => handleFileChange(e, "brief")}
                    style={{ display: "none" }}
                    accept=".xlsx,.xls"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div className="preview-panel" ref={previewPanelRef}>
          {!(
            (uploadedFiles.storyboard && uploadResponses.storyboard) ||
            (uploadedFiles.manuscript && uploadResponses.manuscript) ||
            (uploadedFiles.brief && uploadResponses.brief) ||
            (figmaDetails.url && uploadResponses.figmaFile)
          ) && (
            <>
              <div>
                <Headline3 className="panel-title">Preview</Headline3>
                <Label2 className="preview-subtitle">
                  As assets are uploaded, preview automatically updates and
                  displays images associated with the uploaded files.
                </Label2>
              </div>
              <div className="preview-area">
                <div className="no-preview">
                  <Icon
                    type="image"
                    size="large"
                    className="preview-placeholder-icon"
                  />
                </div>
              </div>
            </>
          )}

          {/* Storyboard Preview */}
          {uploadedFiles.storyboard && uploadResponses.storyboard && (
            <div className="preview-section" ref={storyboardLoaderRef}>
              <div
                className="preview-header-sticky"
                onClick={() => setStoryboardExpanded((prev) => !prev)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Headline3 className="preview-section-label">
                    Storyboard Preview
                  </Headline3>
                  <Label2 className="preview-section-subtext">
                    Images extracted from the uploaded storyboard file displayed
                    in their actual sizes
                  </Label2>
                </div>
                <Icon
                  type={storyboardExpanded ? "chevron-up" : "chevron-down"}
                  className="preview-toggle-icon"
                  style={{ cursor: "pointer", color: "#BE2BBB" }}
                />
              </div>
              {storyboardExpanded &&
                (previewLoading.storyboard ? (
                  <div className="preview-section-loader">
                    <Loader style="inline" />
                  </div>
                ) : (
                  <StoryboardPreview
                    previewData={previewData.storyboard}
                    onFrameSelectionChange={handleFrameSelectionChange}
                  />
                ))}
            </div>
          )}

          {/* Manuscript Preview */}
          {uploadedFiles.manuscript && uploadResponses.manuscript && (
            <div className="preview-section" ref={manuscriptLoaderRef}>
              <div
                className="preview-header-sticky"
                onClick={() => setManuscriptExpanded((prev) => !prev)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Headline3 className="preview-section-label">
                    Manuscript Preview
                  </Headline3>
                  <Label2 className="preview-section-subtext">
                    Content extracted from the uploaded manuscript file
                  </Label2>
                </div>
                <Icon
                  type={manuscriptExpanded ? "chevron-up" : "chevron-down"}
                  className="preview-toggle-icon"
                  style={{ cursor: "pointer", color: "#BE2BBB" }}
                />
              </div>
              {manuscriptExpanded &&
                (previewLoading.manuscript ? (
                  <div className="preview-section-loader">
                    <Loader style="inline" />
                  </div>
                ) : (
                  <ManuscriptPreview
                    previewData={previewData.manuscript}
                    // manuscriptPreviewContent={manuscriptPreviewContent}
                  />
                ))}
            </div>
          )}

          {/* Brief Preview */}
          {uploadedFiles.brief && uploadResponses.brief && (
            <div className="preview-section" ref={briefLoaderRef}>
              <div
                className="preview-header-sticky"
                onClick={() => setBriefSheetExpanded((prev) => !prev)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Headline3 className="preview-section-label">
                    Brief Preview
                  </Headline3>
                  <Label2 className="preview-section-subtext">
                    Size details extracted from the uploaded brief file
                  </Label2>
                </div>
                <Icon
                  type={briefSheetExpanded ? "chevron-up" : "chevron-down"}
                  className="preview-toggle-icon"
                  style={{ cursor: "pointer", color: "#BE2BBB" }}
                />
              </div>
              {briefSheetExpanded &&
                (previewLoading.brief ? (
                  <div className="preview-section-loader">
                    <Loader style="inline" />
                  </div>
                ) : (
                  <BriefPreview previewData={previewData.brief} />
                ))}
            </div>
          )}

          {/* Figma Preview
          {figmaDetails.url && uploadResponses.figmaFile && (
            <FigmaPreview sampleImages={sampleImages} />
          )} */}
        </div>
      </div>

      {/* Upload Progress Modal */}
      <UploadModal
        isOpen={uploadModal.isOpen}
        uploads={uploadModal.uploads}
        onCancel={handleCancelUploads}
        onConfirm={handleConfirmUploads}
        onRemove={handleRemoveUpload}
        onAddDocument={handleAddDocumentFromModal}
      />

      {/* Select Checks Modal */}
      <SelectChecksModal
        open={selectChecksPopupOpen}
        onClose={() => setSelectChecksPopupOpen(false)}
        // isCreatingTask={creatingTask}
        // fteStoryboardTypeLoading={fteStoryboardTypeLoading}
        selectedBrand={selectedBrand}
        selectedChannel={selectedChannel}
        uploadedFiles={uploadedFiles}
        // isAnimatedBannerStoryboardReady={isAnimatedBannerStoryboardReady}
        roleSelection={roleSelection}
        figmaDetails={figmaDetails}
        uploadRequirements={uploadRequirements}
        uploadResponses={uploadResponses}
        animatedBannerFrames={animatedBannerFrames}
        staticBannerFrames={staticBannerFrames}
        fteStoryboardType={fteStoryboardType}
        // selectedBrandObj={selectedBrand}
        // selectedChannelObj={selectedChannel}
        sessionDetails={sessionDetails}
        roleName={roleName}
        moduleAccessToken={moduleAccessToken}
      />
    </div>
  );
};

export default FileUploads;
